import { initialStatisticalAnalysisDelaysAndResponseTimeGeneralState, StatisticalAnalysisDelaysAndResponseTimeGeneralComboItems, StatisticalAnalysisDelaysAndResponseTimeGeneralSelectionDetail, StatisticalAnalysisDelaysAndResponseTimeGeneralState } from '@/modules/statistical-analysis-module/components/statistical-analysis-delays-and-response-time-general/statistical-analysis-delays-and-response-time-general.model'
import StatisticalAnalysisSkeletonDelaysAndResponseTimesDossierTrackingItems from '@/modules/statistical-analysis-module/components/statistical-analysis-skeletons/statistical-analysis-skeleton-delays-and-response-times-dossier/statistical-analysis-skeleton-delays-and-response-times-dossier-tracking-items/statistical-analysis-skeleton-delays-and-response-times-dossier-tracking-items.vue'
import { StatisticalAnalysisDelayAndResponseTimesDetailRequest, StatisticalAnalysisDelayAndResponseTimesDossierResponseContentDelayStats, StatisticalAnalysisDelayAndResponseTimesDossierResponseContentStats, StatisticalAnalysisDelayAndResponseTimesDossierResponseContentTimeStats } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.dto'
import { delayAndResponseTimesDossierService } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.service'
import { StatisticalAnalysisHeaderNavigationItems } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-header/statistical-analysis-header.models'
import { StatisticalInfoPaginatorTrackingItems } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-info-paginator/statistical-analysis-info-paginator.model'
import { StatisticalAnalysisTemporalFilterOptions } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import StatisticalAnalysisTemporalFilter from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.vue'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum, StatisticalAnalysisEnumsAndKeys } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo } from '@/modules/statistical-analysis-module/views/delays-and-response-time-dossier/delays-and-response-time-dossier.model'
import { Nullable } from '@/shared/types/app.types'
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator'

@Component({
    components: {
        StatisticalAnalysisTemporalFilter,
        StatisticalAnalysisSkeletonDelaysAndResponseTimesDossierTrackingItems
    }
})
export default class StatisticalAnalysisDelaysAndResponseTimeGeneral extends Vue {
    @Prop() readonly externalInfo!: StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
    @Emit()
    selectedResponseTime(value: StatisticalAnalysisDelaysAndResponseTimeGeneralSelectionDetail): StatisticalAnalysisDelaysAndResponseTimeGeneralSelectionDetail {
        return value
    }
    @Emit()
    typeDelay(value: Nullable<StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum>): Nullable<StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum> {
        return value
    }
    @Watch('externalInfo', { deep: true, immediate: true })
    onExternalInfoChanged(itemSelected: Nullable<StatisticalAnalysisHeaderNavigationItems<number>>) {
        if (itemSelected) {
            this.$nextTick(() => {
                this.state.promiseViewTracking = this._getContentData()
            })
        }
    }

    state: StatisticalAnalysisDelaysAndResponseTimeGeneralState
    constructor() {
        super()
        this.state = initialStatisticalAnalysisDelaysAndResponseTimeGeneralState()
    }

    get isResponseTimeCompleted(): boolean {
        return this.state.responseTimeSelector.selected.id === StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum.COMPLETED
    }

    get isDelay(): boolean {
        return this.externalInfo.typeDelaysAndResponseTimes!.id === StatisticalAnalysisEnumsAndKeys.DELAY.getId()
    }

    get entitySelected(): string {
        return this.$tc(this.externalInfo.typeSearch?.text as string, 2)
    }

    getTranslatedLabel(key: StatisticalAnalysisDelaysAndResponseTimeGeneralComboItems): string {
        return this.$tc(key.text)
    }

    onShowSpecificView(item: StatisticalAnalysisDelayAndResponseTimesDossierResponseContentStats): void {
        const selection: StatisticalAnalysisDelaysAndResponseTimeGeneralSelectionDetail = {
            trackingItems: this._transformTrackingItems(),
            itemSelected: {
                key: item.responseTimeType.code,
                label: item.responseTimeType.translatedName,
                value: item.countTotal
            },
            delayType: this.state.responseTimeSelector.selected,
            timeFilter: this.state.temporalFilter
        }
        this._updateTypeDelay()
        this.selectedResponseTime(selection)
    }

    onChangeResponseTimeSelector(value: StatisticalAnalysisDelaysAndResponseTimeGeneralComboItems): void {
        this.state.responseTimeSelector.selected = value
        this.state.promiseViewTracking = this._getContentData()
    }

    onTemporalFilter(value: StatisticalAnalysisTemporalFilterOptions): void {
        this.state.temporalFilter = value
        this.state.promiseViewTracking = this._getContentData()
    }

    private _getDetailRequestParams(): StatisticalAnalysisDelayAndResponseTimesDetailRequest {
        let request: StatisticalAnalysisDelayAndResponseTimesDetailRequest = {
            infoDetail: this.state.temporalFilter.id
        }
        if (this.isDelay) {
            request = {
                ...request,
                delayType: this.state.responseTimeSelector.selected.id as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum
            }
        }
        if (this.externalInfo.trackingGeneralSelected?.key) {
            request = {
                ...request
            }
            const typeCase = this.externalInfo.typeSearch?.id as StatiscalAnalysisTypeCases
            switch (typeCase) {
            case StatiscalAnalysisTypeCases.CASE:
                request = { ...request, caseTypeId: this.externalInfo.trackingGeneralSelected.key }
                break
            case StatiscalAnalysisTypeCases.SAMPLE:
                request = { ...request, sampleTypeId: this.externalInfo.trackingGeneralSelected.key }
                break
            case StatiscalAnalysisTypeCases.PORTION:
                request = { ...request, portionTypeId: this.externalInfo.trackingGeneralSelected.key }
                break
            case StatiscalAnalysisTypeCases.SUBPORTION:
                request = { ...request, subportionTypeId: this.externalInfo.trackingGeneralSelected.key }
                break
            default:
                request = { ...request }
            }
        }
        return request
    }

    private async _getContentData(): Promise<void> {
        const response: StatisticalAnalysisDelayAndResponseTimesDossierResponseContentDelayStats[] | StatisticalAnalysisDelayAndResponseTimesDossierResponseContentTimeStats[] = await this.$_toCallSecure<StatisticalAnalysisDelayAndResponseTimesDossierResponseContentDelayStats[] | StatisticalAnalysisDelayAndResponseTimesDossierResponseContentTimeStats[]>(delayAndResponseTimesDossierService.getGeneralViewContent(
            this.isDelay,
            this.externalInfo.typeSearch!.id as StatiscalAnalysisTypeCases,
            delayAndResponseTimesDossierService.getBaseRequest(this.externalInfo),
            this._getDetailRequestParams()
        )) as StatisticalAnalysisDelayAndResponseTimesDossierResponseContentDelayStats[] | StatisticalAnalysisDelayAndResponseTimesDossierResponseContentTimeStats[]
        if (response.length) {
            this.state.trackingItems = response
        } else {
            throw new Error(this.$tc('global.noDataAvailable'))
        }
    }

    private _transformTrackingItems(): StatisticalInfoPaginatorTrackingItems[] {
        return (this.state.trackingItems as StatisticalAnalysisDelayAndResponseTimesDossierResponseContentStats[]).map((item: StatisticalAnalysisDelayAndResponseTimesDossierResponseContentStats) => {
            return {
                key: item.responseTimeType.code,
                label: item.responseTimeType.translatedName,
                value: item.countTotal
            }
        })
    }

    private _updateTypeDelay(): void {
        if (this.externalInfo.typeDelaysAndResponseTimes!.id === StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeSelectorEnum.DELAY) {
            this.typeDelay(this.state.responseTimeSelector.selected.id as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum)
        } else {
            this.typeDelay(null)
        }
    }
}
